import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/auth-context";
import { useCurrentUser, currentUserQueryKey } from "@/hooks/queries/useCurrentUser";
import { ChangeEmailDialog } from "@/components/account/ChangeEmailDialog";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api-client";
import { CLINICIAN_ROLES } from "@/routes/route-access";

// Story 10.9 — Screen 14 "Professional details": four read-only fields. Identity from
// the JWT (auth context); org display from the extended /auth/me (org_id resolves the
// PERSONAL org until the E14 membership model).
//
// Story 14.10 — Email is still a read-only INPUT (it is not editable in place),
// but it now carries an edit affordance that opens the shared self-service
// dialog. Role / Organization / User type stay affordance-free: they are
// administered, not self-served.
//
// Story 23.3 (FR89) — Association membership: clinician-only, editable inline.

// Display-cased role labels — a fixed map keyed by the canonical roles, NOT role
// branching logic (no behavior forks on role here; unknown roles render verbatim).
const ROLE_LABELS: Record<string, string> = {
  clinician: "Clinician",
  org_admin: "Org admin",
  super_admin: "Super admin",
};

// JWT user_origin → the mockup-fixed user-type copy; missing/unknown → em dash.
const USER_TYPE_LABELS: Record<string, string> = {
  webapp_legacy: "Legacy AMPS-certified clinician",
  learnworlds_new: "AMPS clinician",
};

// FR89 — association value → display label used both in read-only view and radio options.
const ASSOCIATION_OPTIONS = [
  { value: "otbc", label: "OTBC member" },
  { value: "saot", label: "SAOT member" },
  { value: "osot", label: "OSOT member" },
  { value: "none", label: "None" },
] as const;

type AssociationValue = (typeof ASSOCIATION_OPTIONS)[number]["value"];

const ASSOCIATION_LABELS: Record<AssociationValue, string> = Object.fromEntries(
  ASSOCIATION_OPTIONS.map(({ value, label }) => [value, label]),
) as Record<AssociationValue, string>;

const EMPTY = "—";

/** Screen 14 org line: personal orgs render as "Personal · {email}", others by name. */
export function formatOrganization(input: {
  orgName: string | null | undefined;
  orgType: string | null | undefined;
  email: string | null | undefined;
}): string {
  if (input.orgType === "personal" && input.email) return `Personal · ${input.email}`;
  return input.orgName ?? EMPTY;
}

// readOnly, not disabled — preserves the label association + text contrast a
// greyed-out disabled input loses for screen readers.
function ReadOnlyField({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: string;
  placeholder?: string;
}) {
  return (
    <FormField label={label}>
      <Input readOnly value={value} placeholder={placeholder} />
    </FormField>
  );
}

interface UpdateAssociationResponse {
  association: AssociationValue;
}

export function ProfessionalDetailsCard() {
  const { user, hasRole } = useAuth();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [changedTo, setChangedTo] = React.useState<string | null>(null);
  const [editingAssociation, setEditingAssociation] = React.useState(false);
  const [selectedAssociation, setSelectedAssociation] =
    React.useState<AssociationValue>("none");

  const role = user?.role ?? "";
  const email = user?.email ?? "";
  const userOrigin = user?.userOrigin ?? null;
  const isClinician = hasRole(...CLINICIAN_ROLES);

  const currentAssociation = (me.data?.association ?? "none") as AssociationValue;

  const associationMutation = useMutation({
    mutationFn: (association: AssociationValue) =>
      apiFetch<UpdateAssociationResponse>("/billing/association", {
        method: "POST",
        body: { association },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: currentUserQueryKey() });
      setEditingAssociation(false);
    },
  });

  function handleOpenEdit() {
    setSelectedAssociation(currentAssociation);
    setEditingAssociation(true);
  }

  function handleAssociationSave() {
    associationMutation.mutate(selectedAssociation);
  }

  function handleAssociationCancel() {
    setEditingAssociation(false);
    associationMutation.reset();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Professional details</CardTitle>
      </CardHeader>
      <div className="mt-4 flex flex-col gap-4">
        {changedTo ? (
          <Banner variant="success">
            Your email address has been updated to {changedTo}. A notification was
            sent to your previous address. Other signed-in sessions have been
            signed out.
          </Banner>
        ) : null}
        <ReadOnlyField label="Role" value={ROLE_LABELS[role] ?? (role || EMPTY)} />
        <ReadOnlyField
          label="Organization"
          // While /auth/me is in flight org data isn't known yet — show a pending
          // placeholder, not the "—" sentinel that would read as "no org".
          value={
            me.isPending
              ? ""
              : formatOrganization({
                  orgName: me.data?.org_name,
                  orgType: me.data?.org_type,
                  email,
                })
          }
          placeholder={me.isPending ? "Loading…" : undefined}
        />
        {/* Story 14.10 — closes seam-109-email-edit: the field stays read-only,
            the change happens in the shared dialog (password-confirmed). */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <ReadOnlyField label="Email" value={email || EMPTY} />
          </div>
          <Button variant="secondary" onClick={() => setDialogOpen(true)}>
            Change email
          </Button>
        </div>
        <ReadOnlyField
          label="User type"
          value={(userOrigin && USER_TYPE_LABELS[userOrigin]) || EMPTY}
        />

        {/* Story 23.3 (FR89) — clinician-only; the backend endpoint enforces
            require_clinician, so non-clinician profiles never show this section. */}
        {isClinician ? (
          <div className="flex flex-col gap-3">
            {!editingAssociation ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                  <ReadOnlyField
                    label="Association membership"
                    value={
                      me.isPending
                        ? ""
                        : ASSOCIATION_LABELS[currentAssociation] ?? EMPTY
                    }
                    placeholder={me.isPending ? "Loading…" : undefined}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleOpenEdit}
                  disabled={me.isPending}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <fieldset className="flex flex-col gap-2">
                  <legend className="text-sm font-medium text-[color:var(--text-primary)]">
                    Association membership
                  </legend>
                  <p className="text-sm text-[color:var(--text-secondary)]">
                    I declare I am a current member
                  </p>
                  <div className="flex flex-col gap-2 pt-1">
                    {ASSOCIATION_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-2.5 text-sm text-[color:var(--text-secondary)]"
                      >
                        <input
                          type="radio"
                          name="association"
                          value={option.value}
                          checked={selectedAssociation === option.value}
                          onChange={() =>
                            setSelectedAssociation(option.value)
                          }
                          className="h-4 w-4 accent-[color:var(--bg-brand-solid)]"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                {associationMutation.isError ? (
                  <Banner variant="danger">
                    Failed to update association. Please try again.
                  </Banner>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    onClick={handleAssociationSave}
                    disabled={associationMutation.isPending}
                  >
                    {associationMutation.isPending ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleAssociationCancel}
                    disabled={associationMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {dialogOpen && email ? (
        <ChangeEmailDialog
          currentEmail={email}
          onCancel={() => setDialogOpen(false)}
          onChanged={(newEmail) => {
            setDialogOpen(false);
            setChangedTo(newEmail);
          }}
        />
      ) : null}
    </Card>
  );
}
